import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Skeleton,
  Alert,
  Button,
  Avatar,
  Paper,
  Chip,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PractitionerSelection = ({ 
  practitioners, 
  selectedPractitioner, 
  onPractitionerSelect,
  onBack,
  loading = false 
}) => {
  const theme = useTheme();
  const defaultImage = 'https://via.placeholder.com/400?text=Practitioner';

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Practitioner
        </Typography>
        <Grid container spacing={3}>
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, height: '100%' }}>
                  <Skeleton variant="rectangular" width="100%" height={200} />
                  <Box sx={{ p: 2, width: '100%' }}>
                    <Skeleton variant="text" width="70%" height={32} />
                    <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="90%" height={20} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!practitioners || practitioners.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2, p: 2 }}>
        No practitioners available for this service. Please contact the clinic directly.
      </Alert>
    );
  }

  // Function to truncate text and add ellipsis
  const truncateText = (text, maxLength = 120) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mr: 2 }}
          color="primary"
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight="medium">
          Choose Your Practitioner
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Any Practitioner Option */}
        <Grid item xs={12} md={6} key="any-practitioner">
          <Card 
            elevation={selectedPractitioner && selectedPractitioner.id === 'any' ? 4 : 1}
            sx={{
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              position: 'relative',
              border: selectedPractitioner && selectedPractitioner.id === 'any' ? `2px solid ${theme.palette.primary.main}` : 'none',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            {selectedPractitioner && selectedPractitioner.id === 'any' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                  borderRadius: '50%',
                  p: 0.5,
                  boxShadow: 2
                }}
              >
                <CheckCircleIcon color="primary" />
              </Box>
            )}
            
            <CardActionArea 
              onClick={() => onPractitionerSelect({
                id: 'any',
                name: 'Any Available Practitioner',
                description: 'Choose this option to see all available time slots across practitioners'
              })}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                alignItems: 'stretch'
              }}
            >
              <Box 
                sx={{ 
                  height: 200, 
                  overflow: 'hidden',
                  backgroundColor: theme.palette.primary.light,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ textAlign: 'center', p: 3, color: 'white' }}>
                  <PersonIcon sx={{ fontSize: 80 }} />
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    Any Practitioner
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Any Available Practitioner
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select this option to see all available appointment times across all practitioners. 
                  You'll see more options, and we'll assign you to the practitioner with the time slot you choose.
                </Typography>
              </CardContent>
            </CardActionArea>
            
            {selectedPractitioner && selectedPractitioner.id === 'any' && (
              <Box sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                p: 1.5,
                textAlign: 'center' 
              }}>
                <Typography fontWeight="medium" color="primary.main">
                  Selected
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Individual Practitioners */}
        {practitioners.map((practitioner) => {
          const isSelected = selectedPractitioner && selectedPractitioner.id === practitioner.id;
          const hasPhoto = !!practitioner.photo_url;
          
          return (
            <Grid item xs={12} md={6} key={practitioner.id}>
              <Card 
                elevation={isSelected ? 4 : 1}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                      borderRadius: '50%',
                      p: 0.5,
                      boxShadow: 2
                    }}
                  >
                    <CheckCircleIcon color="primary" />
                  </Box>
                )}
                
                <CardActionArea 
                  onClick={() => onPractitionerSelect(practitioner)}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%',
                    alignItems: 'stretch'
                  }}
                >
                  {/* Practitioner Photo */}
                  <Box 
                    sx={{ 
                      height: 200, 
                      overflow: 'hidden',
                      backgroundColor: theme.palette.grey[100]
                    }}
                  >
                    {hasPhoto ? (
                      <CardMedia
                        component="img"
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          objectPosition: 'center',
                          backgroundColor: theme.palette.grey[100]
                        }}
                        image={practitioner.photo_url || defaultImage}
                        alt={practitioner.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultImage;
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.grey[400],
                          backgroundColor: theme.palette.grey[100]
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 80 }} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          No photo available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Practitioner Info */}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {practitioner.name}
                    </Typography>
                    
                    {practitioner.title && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <WorkIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          {practitioner.title}
                        </Typography>
                      </Box>
                    )}
                    
                    {practitioner.specialties && (
                      <Box sx={{ mb: 2 }}>
                        {practitioner.specialties.split(',').map((specialty, index) => (
                          <Chip 
                            key={index}
                            label={specialty.trim()}
                            size="small"
                            sx={{ 
                              mr: 0.5, 
                              mb: 0.5,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {practitioner.description && (
                      <>
                        <Divider sx={{ mt: 1, mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {truncateText(practitioner.description)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </CardActionArea>
                
                {isSelected && (
                  <Box sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    p: 1.5,
                    textAlign: 'center' 
                  }}>
                    <Typography fontWeight="medium" color="primary.main">
                      Selected
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PractitionerSelection;